<template>
  <span :class="b(modifiers)">
    <select v-bind="$attrs"
            :class="b('select', { active: internalValue?.length > 0 })"
            :value="internalValue"
            @change="onChange"
            @mouseenter="hover = true"
            @mouseleave="hover = false"
            @focus="$emit('focus')"
    >
      <option v-if="placeholder"
              :disabled="!hasSelectablePlaceholder"
              value=""
      >
        {{ placeholder === true ? $t('e-select.chooseOption') : placeholder }}
      </option>
      <option v-for="option in options"
              :key="`${option[valueField]}`"
              :value="option[valueField]"
              :selected="option[valueField] === internalValue"
      >
        {{ option[labelField] }}
      </option>
    </select>
    <span :class="b('icon-wrapper')">
      <e-icon v-if="hasDefaultState && !focus"
              :class="b('arrow-icon')"
              icon="i-arrow--down"
              size="15"
              inline
      />
      <e-icon v-else
              :class="b('state-icon')"
              :icon="stateIcon"
              size="20"
      />
      <span v-if="progress" :class="b('progress-container')">
        <e-loader variant="tiny" negative />
      </span>
    </span>
  </span>
</template>

<script lang="ts">
  import { defineComponent, PropType, toRefs } from 'vue';
  import useFormStates, { FormStates, withProps } from '@/compositions/form-states';
  import { Modifiers } from '@/plugins/vue-bem-cn/src/globals';
  import eIcon from '@/elements/e-icon.vue';
  import eLoader from '@/elements/e-loader.vue';

  interface Data {
    internalValue: string;
  }

  export interface Options {
    [key: string]: string;
  }

  /**
   * Renders a styled select element. Options can be passed with the `options` property.
   */
  export default defineComponent({
    name: 'e-select',

    components: {
      eIcon,
      eLoader,
    },
    inheritAttrs: false,

    props: {
      ...withProps(),

      /**
       * Value passed by v-model
       */
      modelValue: {
        default: null,
        type: String,
      },

      /**
       * 'options' defines the options which are rendered in the select.
       *
       * e.g. `[{ <valueField>: 'id1', <labelField>: 'Label 1' },{ <valueField>: 'id2', <labelField>: 'Label 2' },...]`
       */
      options: {
        required: true,
        type: Array as PropType<Record<string, unknown>[]>,
      },

      /**
       * The text to display if no option is selected by default.
       * The placeholder can also be disabled by passing 'false' to this prop.
       */
      placeholder: {
        type: [String, Boolean],
        default: true,
      },

      /**
       * Defines if the placeholder text is selectable to reset the select (empty value).
       */
      hasSelectablePlaceholder: {
        type: Boolean,
        default: false,
      },

      /**
       * Shows a progress bar.
       */
      progress: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to change the default field, from which the value is taken for each option.
       */
      valueField: {
        type: String,
        default: 'value',
      },

      /**
       * Allows to change the default field, from which the label text is taken for each option.
       */
      labelField: {
        type: String,
        default: 'label',
      },
    },

    emits: {
      'update:modelValue': (payload: string) => typeof payload === 'string',
      'focus': () => true,
    },

    setup(props): FormStates {
      return {
        ...useFormStates(toRefs(props).state),
      };
    },

    data(): Data {
      return {
        internalValue: this.modelValue,
      };
    },

    computed: {
      /**
       * Defines state modifier classes.
       */
      modifiers(): Modifiers {
        return {
          ...this.stateModifiers,
        };
      },
    },
    watch: {
      /**
       * Updates internal value if model value got changed from parent.
       */
      modelValue(value: string) {
        if (value !== this.internalValue) {
          this.internalValue = value;
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Emits input event for v-model.
       */
      onChange(event: Event) {
        const select = event.currentTarget as HTMLSelectElement;

        this.internalValue = select.value;

        /**
         * input event fires on input
         */
        this.$emit('update:modelValue', select.value);
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '../setup/scss/mixins';
  @use '../setup/scss/variables';

  .e-select {
    $this: &;
    $e-select-height: 30px;

    position: relative;
    display: block;

    &__select {
      @include mixins.icon(arrow--down, 22px, right 5px center, $mask: false); // FF does not support mask on <select>.

      display: flex;
      align-items: center;
      width: 100%;
      height: $e-select-height;
      outline: none;
      border-bottom: 2px solid variables.$color-grayscale--0;
      cursor: pointer;
      font-weight: initial;
      appearance: none;

      &::-ms-expand {
        display: none;
      }

      &--active {
        color: variables.$color-primary--1;
        font-weight: variables.$font-weight--bold;
      }
    }

    // separator for state icons
    select:disabled ~ &__icon-wrapper {
      color: variables.$color-grayscale--400;
    }

    &__icon-wrapper {
      position: absolute;
      top: 50%;
      right: variables.$spacing--0;
      transform: translateY(-50%);
      pointer-events: none;
    }

    // focus
    &__select:focus,
    &--focus &__select {
      outline: none;
      color: variables.$color-primary--1;
    }

    // disabled
    &__select:disabled,
    &--disabled &__select,
    &--disabled &__select:hover {
      border-color: variables.$color-grayscale--400;
      color: variables.$color-grayscale--400;
    }

    /**
     * states
     */
    /* stylelint-disable no-descending-specificity */
    &--state-error {
      color: variables.$color-status--error;

      #{$this}__select:not(:disabled) {
        @include mixins.icon(error, 22px, right 5px center, false); // FF does not support mask on <select>.

        border-color: variables.$color-status--error;
        color: currentColor;
      }
    }

    &--state-success {
      #{$this}__select {
        @include mixins.icon(check, 22px, right 5px center, false); // FF does not support mask on <select>.
      }
    }

    &__progress-container {
      position: absolute;
      top: 50%;
      right: 30px;
      transform: translateY(-50%);
    }
  }
</style>
